import { FC, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  TextField,
} from "@mui/material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import { useMutation } from "react-query";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Cancel";
import { Asterisk } from "../../common/components/Asterisk";
import { SectionTitle } from "../../common/components/SectionTitle";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { ButtonSubmitA } from "../../common/components/ButtonSubmitA";
import { fetchCreateParentCompany } from "../miscellaneous/fetchCreateParentCompany";
import { fetchAvailableOrganizations } from "../miscellaneous/fetchAvailableOrganizations";
import { fetchAvailableClientAdmins } from "../miscellaneous/fetchAvailableClientAdmins";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

const formControlStyle = { marginTop: "20px" };
const btnGroupStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  gap: "20px",
  marginTop: "60px",
};

export const CreateParentCompany: FC = () => {
  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();
  const navigate = useNavigate();
  const [selectedOrgs, setSelectedOrgs] = useState<any[]>([]);
  const [selectedAdmins, setSelectedAdmins] = useState<any[]>([]);
  const [availableOrgs, setAvailableOrgs] = useState<any[]>([]);
  const [availableAdmins, setAvailableAdmins] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState("");

  const mutateLoadOrgs = useMutation(fetchAvailableOrganizations, {
    onSuccess: (res) => setAvailableOrgs(res.data),
    onError: () => {},
  });

  const mutateLoadAdmins = useMutation(fetchAvailableClientAdmins, {
    onSuccess: (res) => setAvailableAdmins(res.data),
    onError: () => {},
  });

  useEffect(() => {
    mutateLoadOrgs.mutate(undefined);
    mutateLoadAdmins.mutate(undefined);
  }, []);

  const mutateCreate = useMutation(fetchCreateParentCompany, {
    onSuccess: (res) => {
      if (res.status === 200) navigate("/company-management");
    },
    onError: (error: any) => {
      if (error?.response?.data) {
        setErrorMessage(error.response.data);
      }
    },
  });

  const onSubmit = (data: any) => {
    setErrorMessage("");
    const payload = {
      companyName: data.companyName ?? "",
      organizationIds: selectedOrgs.map((o) => o.id),
      clientAdminIds: selectedAdmins.map((a) => a.id),
    };
    mutateCreate.mutate(payload);
  };

  const handleCancel = () => navigate("/company-management");

  return (
    <div className="mt-9">
      <SectionTitle name="Setup Company" />
      <Box sx={{ minWidth: 120, marginTop: "60px" }}>
        <form className="pr-24 pl-24 pb-24" onSubmit={handleSubmit(onSubmit)}>
          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Company <Asterisk />
            </label>
            <TextField
              {...register("companyName", { required: "Company is required" })}
              defaultValue={""}
              sx={{ "& .MuiInputBase-input": { height: "5px" } }}
            />
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("companyName").error?.message}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">Organization</label>
            <Autocomplete
              multiple
              options={availableOrgs}
              disableCloseOnSelect
              value={selectedOrgs}
              onChange={(_, newValue) => setSelectedOrgs(newValue)}
              getOptionLabel={(option: any) => option.companyName}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.companyName}
                </li>
              )}
              renderInput={(params) => <TextField {...params} />}
            />
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">Client Admin</label>
            <Autocomplete
              multiple
              options={availableAdmins}
              disableCloseOnSelect
              value={selectedAdmins}
              onChange={(_, newValue) => setSelectedAdmins(newValue)}
              getOptionLabel={(option: any) =>
                option.fullName ? `${option.fullName} (${option.email})` : option.email
              }
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={icon}
                    checkedIcon={checkedIcon}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {option.fullName ? `${option.fullName} (${option.email})` : option.email}
                </li>
              )}
              renderInput={(params) => <TextField {...params} />}
            />
          </FormControl>

          {errorMessage && (
            <FormHelperText sx={{ color: "red", marginTop: "12px" }}>
              {errorMessage}
            </FormHelperText>
          )}

          <FormControl fullWidth sx={btnGroupStyle}>
            <ButtonCancelA
              name="Cancel"
              onHandleClick={handleCancel}
              icon={<CancelIcon sx={{ marginRight: "4px" }} />}
            />
            <ButtonSubmitA
              name="Save"
              icon={<SaveIcon sx={{ marginRight: "4px" }} />}
            />
          </FormControl>
        </form>
      </Box>
    </div>
  );
};
